import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import { usePermissions } from '../../../hooks/usePermissions';
import { createMarket } from '../../../services/markets/createMarket';
import { getMarketDetails } from '../../../services/markets/getMarketDetails';
import { getMarketList } from '../../../services/markets/getMarketList';
import { updateMarket } from '../../../services/markets/updateMarket';
import { stateOptions } from '../../../utils/stateOptions';

interface MarketFormValues {
  name: string;
  taxid: string;
  tablespace: string;
  state: string;
  pacPhone: string;
  active: boolean;
  external: boolean;
}

interface Resource {
  title: string;
  link: string;
  type: 'simple' | 'nested';
  subItems?: { key: string; value: string }[];
  originalKey?: string; // Store the original API field name
}

const CreateMarket = () => {
  const {
    register,
    unregister,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<MarketFormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      taxid: '',
      tablespace: '',
      state: '',
      pacPhone: '',
      active: true,
      external: false,
    },
  });

  const isExternal = watch('external');

  useEffect(() => {
    if (isExternal) {
      unregister('taxid');
      unregister('tablespace');
    }
  }, [isExternal, unregister]);

  const { id: marketId } = useParams<{ id: string }>();
  const isEditMode = !!marketId;

  // State for fetched data
  const [marketData, setMarketData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError, setMarketError] = useState<string | null>(null);

  const toast = useToast();
  const navigate = useNavigate();
  const cancelDialog = useDisclosure();
  const cancelRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inactiveConfirmDialog = useDisclosure();
  const inactiveConfirmRef = useRef(null);
  const pendingSubmitData = useRef<MarketFormValues | null>(null);

  const getSafeSelectValue = (value: unknown) => {
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'string' || typeof value === 'number')
      return value;
    return '';
  };
  const [resources, setResources] = useState<Resource[]>([]);

  // Get user permissions
  const { isAdmin } = usePermissions();

  // Fetch market data if in edit mode
  const fetchMarket = useCallback(async () => {
    if (!marketId) return;

    setMarketLoading(true);
    setMarketError(null);

    try {
      // Try getMarketDetails first
      const { data } = await getMarketDetails(marketId);

      let marketData: Record<string, unknown> | null = null;

      if (data?.market && Object.keys(data.market).length > 0) {
        // Use getMarketDetails response
        marketData = data.market as unknown as Record<
          string,
          unknown
        >;
      } else {
        // Fallback to getMarketList
        const listResponse = await getMarketList([marketId]);

        if (listResponse.data?.states) {
          // Find the market in the states array
          for (const state of listResponse.data.states) {
            const foundMarket = state.markets.find(
              (market) => market.id === marketId
            );
            if (foundMarket) {
              marketData = foundMarket as unknown as Record<
                string,
                unknown
              >;
              break;
            }
          }
        }
      }

      if (marketData && Object.keys(marketData).length > 0) {
        setMarketData(marketData);

        // Clear any existing error since we have valid data
        setMarketError(null);
      } else {
        setMarketError(
          'Market not found - please check the market ID'
        );
      }
    } catch (error) {
      setMarketError('Failed to load market data');
      toast({
        title: 'Error loading market',
        description: 'Failed to load market data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMarketLoading(false);
    }
  }, [marketId, toast]);

  useEffect(() => {
    if (isEditMode) {
      fetchMarket();
    }
  }, [isEditMode, fetchMarket, marketId]);

  // Populate form when marketData is available
  useEffect(() => {
    if (
      isEditMode &&
      marketData &&
      Object.keys(marketData).length > 0
    ) {
      // Handle state field - convert full state name to abbreviation if needed
      let stateValue =
        (marketData.state as string) ||
        (marketData.location as string) ||
        '';

      // If state is a full name, try to find the abbreviation
      if (stateValue && stateValue.length > 2) {
        const stateOption = stateOptions.find(
          (option) =>
            option.label.toLowerCase() === stateValue.toLowerCase()
        );
        if (stateOption) {
          stateValue = stateOption.value;
        }
      }

      const formData = {
        name: (marketData.name as string) || '',
        taxid: (marketData.taxid as string) || '',
        tablespace: (marketData.tablespace as string) || '',
        state: stateValue,
        pacPhone: (marketData.pacPhone as string) || '',
        active: (marketData.active as boolean) ?? true,
        external: (marketData.external as boolean) ?? false,
      };

      // Use setValue directly for more reliable form population
      setValue('name', formData.name);
      setValue('taxid', formData.taxid);
      setValue('tablespace', formData.tablespace);
      setValue('state', formData.state);
      setValue('pacPhone', formData.pacPhone);
      setValue('active', formData.active);
      setValue('external', formData.external);

      // Set resources from existing data
      if (marketData.resource) {
        const existingResources: Resource[] = [];
        const resource = marketData.resource as Record<
          string,
          unknown
        >;

        Object.entries(resource).forEach(([key, value]) => {
          // Check if key is all caps (likely an acronym) and preserve it
          // Handle both with and without underscores
          // Remove length restriction to allow multi-word acronyms
          const isAllCapsKey =
            key === key.toUpperCase() &&
            /^[A-Z0-9_]+$/.test(key);

          let title: string;
          if (isAllCapsKey) {
            // Preserve acronyms - convert underscores back to spaces for display
            // Keep all caps exactly as stored
            title = key.replace(/_/g, ' ');
          } else {
            // Convert camelCase key to readable title, preserving all-caps words
            // Split camelCase by detecting capital letters
            // Handle cases like "PACLead" where "PAC" should stay uppercase as one word
            const words: string[] = [];
            let currentWord = '';
            let inAcronym = false; // Track if we're in an acronym (consecutive uppercase)

            for (let i = 0; i < key.length; i++) {
              const char = key[i];
              const isUpperCase = /[A-Z]/.test(char);
              const isLowerCase = /[a-z]/.test(char);
              const nextChar = i < key.length - 1 ? key[i + 1] : null;
              const nextIsLowerCase = nextChar ? /[a-z]/.test(nextChar) : false;

              if (isUpperCase) {
                if (currentWord.length === 0) {
                  // Start of key - begin new word
                  currentWord = char;
                  // Check if this starts an acronym (multiple uppercase) or single word
                  inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
                } else if (isLowerCase && /[a-z]/.test(currentWord[currentWord.length - 1])) {
                  // Transition from lowercase to uppercase - start new word
                  words.push(currentWord);
                  currentWord = char;
                  inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
                } else if (inAcronym && isUpperCase && nextIsLowerCase) {
                  // Uppercase in acronym but next is lowercase - end acronym, start new word
                  words.push(currentWord);
                  currentWord = char;
                  inAcronym = false;
                } else if (inAcronym && isUpperCase) {
                  // Continue acronym (consecutive uppercase)
                  currentWord += char;
                } else if (!inAcronym && isUpperCase) {
                  // New uppercase after lowercase - start new word
                  words.push(currentWord);
                  currentWord = char;
                  inAcronym = nextChar ? /[A-Z]/.test(nextChar) : false;
                } else {
                  currentWord += char;
                }
              } else if (isLowerCase) {
                if (inAcronym && currentWord.length > 1) {
                  // Acronym ended, start new word with this lowercase
                  words.push(currentWord);
                  currentWord = char;
                  inAcronym = false;
                } else if (inAcronym && currentWord.length === 1) {
                  // Single uppercase followed by lowercase - not an acronym, continue word
                  currentWord += char;
                  inAcronym = false;
                } else {
                  // Continue current word
                  currentWord += char;
                }
              } else {
                // Numbers or other characters
                currentWord += char;
              }
            }
            if (currentWord.length > 0) {
              words.push(currentWord);
            }

            // Reconstruct title with proper spacing and capitalization
            title = words
              .map((word, index) => {
                // Check if word is all caps (preserve acronyms like "PAC")
                const isAllCaps = /^[A-Z0-9]+$/.test(word) && word === word.toUpperCase();
                if (isAllCaps && word.length > 1) {
                  // Preserve all-caps acronyms exactly
                  return word;
                } else if (index === 0) {
                  // First word: capitalize first letter only
                  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }
                // Subsequent words: preserve capitalization
                return word;
              })
              .join(' ')
              .trim();
          }

          if (typeof value === 'string' && value.trim() !== '') {
            // Simple string value (URL or text)
            existingResources.push({
              title,
              link: value,
              type: 'simple',
              originalKey: key, // Preserve original API field name
            });
          } else if (typeof value === 'object' && value !== null) {
            // Nested object with multiple key-value pairs
            const subItems = Object.entries(
              value as Record<string, unknown>
            )
              .filter(
                ([, subValue]) =>
                  typeof subValue === 'string' &&
                  subValue.trim() !== ''
              )
              .map(([subKey, subValue]) => ({
                key: subKey,
                value: subValue as string,
              }));

            if (subItems.length > 0) {
              existingResources.push({
                title,
                link: '', // Not used for nested resources
                type: 'nested',
                subItems,
                originalKey: key, // Preserve original API field name
              });
            }
          }
        });
        setResources(existingResources);
      }
    }
  }, [isEditMode, marketData, setValue]);

  const onSubmit = async (data: MarketFormValues) => {
    if (!isAdmin()) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to edit markets.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if market is being set to inactive and user hasn't confirmed yet
    if (!data.active && !inactiveConfirmDialog.isOpen) {
      pendingSubmitData.current = data;
      inactiveConfirmDialog.onOpen();
      return;
    }

    setIsSubmitting(true);

    try {
      // Build resource object from resources array
      const resourceObj: Record<
        string,
        string | Record<string, string>
      > = {};

      // Mapping for common resource titles to API field names
      const titleToApiKey: Record<string, string> = {
        'Job Aids': 'jobAids',
        Scripts: 'scripts',
        'Home Page': 'homePage',
        'Issues Form': 'issuesForm',
        '911 Protocol': '911Protocol',
        'Training Folder': 'trainingFolder',
        'Athena Downtime Form': 'athenaDowntimeForm',
        'Central Billing Number': 'centralBillingNumber',
        'Patient Portal Finder': 'patientPortalFinder',
        'Physician Directory Link': 'PhysicianDirectoryLink',
        'Covid Screening Required': 'covidScreeningRequired',
        'Interpreter Line And Code': 'interpreterLineAndCode',
        'Patient Outreach Campaigns': 'patientOutreachCampaigns',
        'Switchboard Operator Number': 'switchboardOperatorNumber',
        'Patient Case Sent To PAC Responses':
          'patientCaseSentToPACResponses',
        'Covid Scheduling Without Screening':
          'covidSchedulingWithoutScreening',
        'Urgent Care': 'urgentCare',
        Multiple: 'multiple',
        PAC: 'PAC', // Preserve capital letters for acronyms
        'PAC ': 'PAC', // Handle trailing space
      };

      resources.forEach((resource) => {
        // Preserve original title capitalization - trim but don't transform
        const trimmedTitle = resource.title.trim();
        if (trimmedTitle) {
          // Generate API key from title to preserve user's capitalization
          // Only use originalKey if title hasn't changed (check by comparing normalized versions)
          let apiKey: string;
          // If originalKey exists and title matches what it would generate, use originalKey
          // Otherwise, regenerate from current title to preserve user's capitalization
          const shouldUseOriginalKey =
            resource.originalKey &&
            resource.title
              .trim()
              .toLowerCase()
              .replace(/\s+/g, '') ===
              resource.originalKey
                .toLowerCase()
                .replace(/_/g, '')
                .replace(/[^a-zA-Z0-9]/g, '');

          if (shouldUseOriginalKey && resource.originalKey) {
            apiKey = resource.originalKey;
          } else {
            // For new resources, try to find a mapping or create camelCase
            // Check both exact match and trimmed match
            if (
              titleToApiKey[trimmedTitle] ||
              titleToApiKey[resource.title]
            ) {
              apiKey =
                titleToApiKey[trimmedTitle] ||
                titleToApiKey[resource.title];
            } else {
              // Check if title is all caps (likely an acronym) and preserve it
              // Preserve whatever capitalization the user entered
              // Check if all alphabetic characters are uppercase (allow spaces and numbers)
              const hasOnlyUpperCaseLetters = /^[A-Z0-9\s]+$/.test(trimmedTitle);
              // Check if title equals its uppercase version (meaning it's all caps)
              // Remove length restriction to allow multi-word acronyms
              const hasNoLowercase = trimmedTitle === trimmedTitle.toUpperCase();
              const isAllCaps =
                hasNoLowercase &&
                hasOnlyUpperCaseLetters &&
                trimmedTitle.length > 0;

              if (isAllCaps) {
                // Preserve acronyms - convert spaces to underscores and keep uppercase
                apiKey = trimmedTitle.replace(/\s+/g, '_').toUpperCase();
              } else {
                // Convert to camelCase for regular titles, but preserve all-caps words
                // Split by spaces and preserve capitalization of all-caps words
                const words = trimmedTitle.split(/\s+/);
                const processedWords = words.map((word, index) => {
                  // Check if word is all caps (preserve acronyms like "PAC")
                  const isAllCaps = /^[A-Z0-9]+$/.test(word) && word === word.toUpperCase();
                  if (isAllCaps && word.length > 0) {
                    // Preserve all-caps words exactly (e.g., "PAC" stays "PAC")
                    return word;
                  } else if (index === 0) {
                    // First word: lowercase
                    return word.toLowerCase();
                  } else {
                    // Subsequent words: capitalize first letter
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                  }
                });
                apiKey = processedWords
                  .join('')
                  .replace(/[^a-zA-Z0-9]/g, '') // Remove special characters
                  .replace(/^[0-9]/, 'resource$&'); // Ensure it doesn't start with a number
              }
            }
          }

          // Ensure we have a valid key
          if (apiKey) {
            if (resource.type === 'simple' && resource.link.trim()) {
              // Simple string value
              resourceObj[apiKey] = resource.link;
            } else if (
              resource.type === 'nested' &&
              resource.subItems &&
              resource.subItems.length > 0
            ) {
              // Nested object with multiple key-value pairs
              const nestedObj: Record<string, string> = {};
              resource.subItems.forEach((subItem) => {
                if (subItem.key.trim() && subItem.value.trim()) {
                  nestedObj[subItem.key] = subItem.value;
                }
              });
              if (Object.keys(nestedObj).length > 0) {
                resourceObj[apiKey] = nestedObj;
              }
            }
          }
        }
      });

      const basePayload = {
        name: data.name,
        location: data.state, // API uses location field
        nickname: data.name, // Using name as nickname for now
        tablespace: data.external ? '' : data.tablespace,
        taxid: data.external ? '' : data.taxid,
        pronunciation: data.name, // Using name as pronunciation for now
        pacPhone: data.pacPhone.trim() || null,
        resource: resourceObj,
        active: data.active,
        external: data.external,
      };

      if (isEditMode && marketId) {
        await updateMarket({ ...basePayload, id: marketId });
        toast({
          title: 'Market updated successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
        navigate(`/markets/${marketId}?refresh=${Date.now()}`);
      } else {
        const { data: response } = await createMarket(basePayload);
        toast({
          title: 'Market created successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
        if (response.marketId) {
          navigate(`/markets/${response.marketId}/practices`);
        }
      }
    } catch (error) {
      toast({
        title: isEditMode
          ? 'Failed to update market'
          : 'Failed to create market',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && marketId) {
      navigate(`/markets/${marketId}`);
    } else {
      navigate('/');
    }
  };

  const handleConfirmInactive = async () => {
    inactiveConfirmDialog.onClose();
    if (pendingSubmitData.current) {
      await onSubmit(pendingSubmitData.current);
    }
  };

  const handleCancelInactive = () => {
    inactiveConfirmDialog.onClose();
    pendingSubmitData.current = null;
  };

  const addResource = () => {
    setResources([
      ...resources,
      { title: '', link: '', type: 'simple' },
    ]);
  };

  const removeResource = (index: number) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const updateResource = (
    index: number,
    field: 'title' | 'link' | 'type',
    value: string
  ) => {
    const updatedResources = [...resources];
    if (field === 'type') {
      updatedResources[index][field] = value as 'simple' | 'nested';
      // Reset subItems when switching to simple type
      if (value === 'simple') {
        updatedResources[index].subItems = undefined;
      } else if (value === 'nested') {
        updatedResources[index].subItems = [{ key: '', value: '' }];
        updatedResources[index].link = ''; // Clear link for nested type
      }
    } else if (field === 'title') {
      // Update title and clear originalKey so it uses the new mapping
      updatedResources[index][field] = value;
      updatedResources[index].originalKey = undefined; // Clear original key to use new mapping
    } else {
      updatedResources[index][field] = value;
    }
    setResources(updatedResources);
  };

  const addSubItem = (resourceIndex: number) => {
    const updatedResources = [...resources];
    if (!updatedResources[resourceIndex].subItems) {
      updatedResources[resourceIndex].subItems = [];
    }
    updatedResources[resourceIndex].subItems!.push({
      key: '',
      value: '',
    });
    setResources(updatedResources);
  };

  const removeSubItem = (
    resourceIndex: number,
    subItemIndex: number
  ) => {
    const updatedResources = [...resources];
    updatedResources[resourceIndex].subItems = updatedResources[
      resourceIndex
    ].subItems!.filter((_, i) => i !== subItemIndex);
    setResources(updatedResources);
  };

  const updateSubItem = (
    resourceIndex: number,
    subItemIndex: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const updatedResources = [...resources];
    updatedResources[resourceIndex].subItems![subItemIndex][field] =
      value;
    setResources(updatedResources);
  };

  if (marketLoading) {
    return <PageLoader />;
  }

  if (marketError) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="50vh"
        gap="1rem"
      >
        <Text fontSize="1.25rem" fontWeight="600" color="red.500">
          {marketError}
        </Text>
        <Button colorScheme="blue" onClick={fetchMarket}>
          Retry
        </Button>
        <Button variant="outline" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isEditMode ? 'Edit Market' : 'Create Market'} - CHS
        </title>
      </Helmet>

      <Box ml="11.25rem" mr="11.25rem" mb="3rem" mt="1.75rem">
        <Flex
          direction="column"
          align="flex-start"
          gap="1rem"
          width="100%"
        >
          <Text
            fontSize="1.875rem"
            fontWeight="700"
            lineHeight={'133%'}
            color="text.primary"
            mb="2rem"
          >
            {isEditMode ? 'Edit Market' : 'Create Market'}
          </Text>

          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: '100%' }}
          >
            <Flex direction="column" gap="1rem">
              {/* Market Information Section */}
              <Text
                fontSize="1.25rem"
                fontWeight="700"
                lineHeight="120%"
                color="text.secondary"
                mb="1.5rem"
              >
                Market Information
              </Text>

              <FormControl isInvalid={!!errors.name}>
                <FormLabel
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  Market Name*
                </FormLabel>
                <Input
                  {...register('name', {
                    required: 'Market name is required',
                  })}
                  placeholder="Enter market name"
                  size="md"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="0.375rem"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
                <FormErrorMessage>
                  {errors.name?.message as string}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  External
                </FormLabel>
                <Controller
                  name="external"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      isChecked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    >
                      <Text fontSize="0.75rem" color="gray.600">
                        Check if this market is for practices and providers outside the
                        health system
                      </Text>
                    </Checkbox>
                  )}
                />
              </FormControl>

              {!isExternal && (
                <Flex gap="1rem">
                  <FormControl isInvalid={!!errors.taxid} flex="1">
                    <FormLabel
                      fontSize="0.75rem"
                      fontWeight="500"
                      lineHeight="1rem"
                    >
                      Tax ID #*
                    </FormLabel>
                    <Input
                      {...register('taxid', {
                        required: 'Tax ID is required',
                      })}
                      placeholder="Enter tax ID"
                      size="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="0.375rem"
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                    />
                    <FormErrorMessage>
                      {errors.taxid?.message as string}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.tablespace} flex="1">
                    <FormLabel
                      fontSize="0.75rem"
                      fontWeight="500"
                      lineHeight="1rem"
                    >
                      Table Space*
                    </FormLabel>
                    <Input
                      {...register('tablespace', {
                        required: 'Table space is required',
                      })}
                      placeholder="Enter table space"
                      size="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="0.375rem"
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                    />
                    <FormErrorMessage>
                      {errors.tablespace?.message as string}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
              )}

              <FormControl isInvalid={!!errors.state}>
                <FormLabel
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  State*
                </FormLabel>
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: 'State is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select state"
                      size="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="0.375rem"
                      value={getSafeSelectValue(field.value)}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                    >
                      {stateOptions.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>
                  {errors.state?.message as string}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  PAC Phone
                </FormLabel>
                <Input
                  {...register('pacPhone')}
                  placeholder="Enter PAC phone"
                  size="md"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="0.375rem"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  fontSize="0.75rem"
                  fontWeight="500"
                  lineHeight="1rem"
                >
                  Market Status
                </FormLabel>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        value={field.value ? 'true' : 'false'}
                        onChange={(e) =>
                          field.onChange(e.target.value === 'true')
                        }
                        placeholder="Select status"
                        size="md"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="0.375rem"
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                      >
                        <option value="true">Active</option>
                        <option value="false">
                          Inactive (Terminated)
                        </option>
                      </Select>
                      {!field.value && (
                        <Alert
                          status="warning"
                          borderRadius="0.375rem"
                          mt={3}
                          fontSize="0.875rem"
                        >
                          <AlertIcon />
                          <AlertDescription>
                            Setting this market to inactive will mark all
                            associated providers as "Not on PAC" and as
                            "Terminated". This action requires confirmation
                            before saving.
                          </AlertDescription>
                        </Alert>
                      )}
                    </>
                  )}
                />
                <FormHelperText fontSize="0.75rem" color="gray.600" mt={2}>
                  Set to "Inactive" to terminate this market
                </FormHelperText>
              </FormControl>

              {/* Resources Section */}
              <Text
                fontSize="1.25rem"
                fontWeight="700"
                lineHeight="120%"
                color="text.secondary"
                mb="1.5rem"
              >
                Resources
              </Text>
              <FormControl>
                <FormHelperText
                  fontSize="0.75rem"
                  color="gray.600"
                  mb="1rem"
                >
                  Add links to important resources, documents, or
                  websites related to this market.
                </FormHelperText>
                {resources.length > 0 && (
                  <Text
                    fontSize="0.75rem"
                    color="blue.600"
                    mb="0.5rem"
                    fontWeight="500"
                  >
                    {resources.length} resource
                    {resources.length !== 1 ? 's' : ''} will be saved
                  </Text>
                )}
              </FormControl>

              <VStack align="stretch" gap="1.5rem">
                {resources.map((resource, index) => (
                  <Box
                    key={index}
                    p="1.5rem"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="0.5rem"
                    bg="gray.50"
                  >
                    <VStack align="stretch" spacing="1rem">
                      {/* Resource Header */}
                      <HStack spacing="1rem" align="flex-end">
                        <FormControl flex="1">
                          <FormLabel
                            fontSize="0.75rem"
                            fontWeight="500"
                            lineHeight="1rem"
                          >
                            Resource Title
                          </FormLabel>
                          <Input
                            value={resource.title}
                            onChange={(e) =>
                              updateResource(
                                index,
                                'title',
                                e.target.value
                              )
                            }
                            placeholder="Enter resource title"
                            size="md"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="0.375rem"
                            _focus={{
                              borderColor: 'blue.500',
                              boxShadow: '0 0 0 1px #3182ce',
                            }}
                          />
                        </FormControl>
                        <FormControl flex="1">
                          <FormLabel
                            fontSize="0.75rem"
                            fontWeight="500"
                            lineHeight="1rem"
                          >
                            Resource Type
                          </FormLabel>
                          <Select
                            value={resource.type}
                            onChange={(e) =>
                              updateResource(
                                index,
                                'type',
                                e.target.value
                              )
                            }
                            size="md"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="0.375rem"
                            _focus={{
                              borderColor: 'blue.500',
                              boxShadow: '0 0 0 1px #3182ce',
                            }}
                          >
                            <option value="simple">
                              Simple Link/Text
                            </option>
                            <option value="nested">
                              Multiple Items
                            </option>
                          </Select>
                        </FormControl>
                        <IconButton
                          aria-label="Remove resource"
                          icon={<DeleteIcon />}
                          size="md"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeResource(index)}
                          alignSelf="flex-end"
                          mb="0.5rem"
                        />
                      </HStack>

                      {/* Simple Resource Type */}
                      {resource.type === 'simple' && (
                        <FormControl>
                          <FormLabel
                            fontSize="0.75rem"
                            fontWeight="500"
                            lineHeight="1rem"
                          >
                            Resource Link/Text
                          </FormLabel>
                          <Input
                            value={resource.link}
                            onChange={(e) =>
                              updateResource(
                                index,
                                'link',
                                e.target.value
                              )
                            }
                            placeholder="Enter URL or text (e.g., phone number)"
                            size="md"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="0.375rem"
                            _focus={{
                              borderColor: 'blue.500',
                              boxShadow: '0 0 0 1px #3182ce',
                            }}
                          />
                        </FormControl>
                      )}

                      {/* Nested Resource Type */}
                      {resource.type === 'nested' && (
                        <VStack align="stretch" spacing="1rem">
                          <HStack
                            justify="space-between"
                            align="center"
                          >
                            <Text
                              fontSize="0.875rem"
                              fontWeight="600"
                              color="gray.700"
                            >
                              Resource Items
                            </Text>
                            <Button
                              size="sm"
                              leftIcon={<AddIcon />}
                              onClick={() => addSubItem(index)}
                              colorScheme="blue"
                              variant="outline"
                            >
                              Add Item
                            </Button>
                          </HStack>

                          {resource.subItems?.map(
                            (subItem, subIndex) => (
                              <HStack
                                key={subIndex}
                                spacing="1rem"
                                align="flex-end"
                                p="1rem"
                                bg="white"
                                borderRadius="0.375rem"
                                border="1px solid"
                                borderColor="gray.200"
                              >
                                <FormControl flex="1">
                                  <FormLabel
                                    fontSize="0.75rem"
                                    fontWeight="500"
                                    lineHeight="1rem"
                                  >
                                    Item Name
                                  </FormLabel>
                                  <Input
                                    value={subItem.key}
                                    onChange={(e) =>
                                      updateSubItem(
                                        index,
                                        subIndex,
                                        'key',
                                        e.target.value
                                      )
                                    }
                                    placeholder="e.g., Key West Medical Center"
                                    size="md"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="0.375rem"
                                    _focus={{
                                      borderColor: 'blue.500',
                                      boxShadow: '0 0 0 1px #3182ce',
                                    }}
                                  />
                                </FormControl>
                                <FormControl flex="2">
                                  <FormLabel
                                    fontSize="0.75rem"
                                    fontWeight="500"
                                    lineHeight="1rem"
                                  >
                                    Item Value
                                  </FormLabel>
                                  <Input
                                    value={subItem.value}
                                    onChange={(e) =>
                                      updateSubItem(
                                        index,
                                        subIndex,
                                        'value',
                                        e.target.value
                                      )
                                    }
                                    placeholder="e.g., URL or phone number"
                                    size="md"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="0.375rem"
                                    _focus={{
                                      borderColor: 'blue.500',
                                      boxShadow: '0 0 0 1px #3182ce',
                                    }}
                                  />
                                </FormControl>
                                <IconButton
                                  aria-label="Remove item"
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() =>
                                    removeSubItem(index, subIndex)
                                  }
                                  alignSelf="flex-end"
                                  mb="0.5rem"
                                />
                              </HStack>
                            )
                          )}
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                ))}

                <Button
                  leftIcon={<AddIcon />}
                  variant="outline"
                  size="md"
                  onClick={addResource}
                  alignSelf="flex-start"
                  colorScheme="blue"
                  borderColor="blue.500"
                  _hover={{
                    bg: 'blue.50',
                    borderColor: 'blue.600',
                  }}
                >
                  Add Resource
                </Button>
              </VStack>

              {/* Submit Button */}
              <Flex
                gap="2rem"
                mt="2rem"
                mb="3rem"
                align="left"
                alignItems={'center'}
                justifyContent={'flex-start'}
              >
                <Button
                  variant="outline"
                  colorScheme="red"
                  height={'3rem'}
                  borderRadius={'0.375rem'}
                  width="7.38rem"
                  onClick={cancelDialog.onOpen}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  p="0 1rem"
                  height={'3rem'}
                  borderRadius={'0.375rem'}
                  background={'background.brandLight'}
                  width="7.38rem"
                  isLoading={isSubmitting}
                  loadingText={
                    isEditMode ? 'Updating...' : 'Creating...'
                  }
                >
                  {isEditMode ? 'Update Market' : 'Create Market'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      </Box>

      {/* Inactive Market Confirmation Dialog */}
      <AlertDialog
        isOpen={inactiveConfirmDialog.isOpen}
        leastDestructiveRef={inactiveConfirmRef}
        onClose={handleCancelInactive}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Set Market to Inactive?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <AlertDescription>
                  Setting this market to inactive will:
                </AlertDescription>
              </Alert>
              <Text fontSize="0.875rem" mb={2}>
                • Mark all providers associated with this market as{' '}
                <strong>"Not on PAC"</strong>
              </Text>
              <Text fontSize="0.875rem" mb={4}>
                • Mark all providers associated with this market as{' '}
                <strong>"Terminated"</strong>
              </Text>
              <Text fontSize="0.875rem" fontWeight="600">
                Do you want to continue?
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={inactiveConfirmRef} onClick={handleCancelInactive}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleConfirmInactive} ml={3}>
                Confirm & Save
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        isOpen={cancelDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel {isEditMode ? 'Edit' : 'Create'} Market
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to cancel? Any unsaved changes
              will be lost.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelDialog.onClose}>
                Continue Editing
              </Button>
              <Button colorScheme="red" onClick={handleCancel} ml={3}>
                Cancel
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default CreateMarket;
